import type { RowDragEndEvent } from 'ag-grid-community'
import type { ViewHook } from 'phoenix_live_view'

/**
 * Row Drag Handlers for AG Grid
 * Handles row reordering with drag-and-drop and Phoenix LiveView synchronization
 */

// Row drag end payload structure matching backend API
type RowDragEndPayload = {
  readonly id: string // Row ID
  readonly position: number // New 1-based position
}

/** Validate that drag params contain required properties */
const hasValidDragParams = (params: RowDragEndEvent): boolean =>
  Boolean(
    params.node?.data?.id != null &&
      params.overNode != null &&
      params.overNode.rowIndex != null
  )

/** Extract row ID from node data with type safety */
const getRowId = (data: unknown): string => {
  if (!data || typeof data !== 'object' || !('id' in data)) {
    throw new Error('Row data missing required "id" property')
  }

  const { id } = data as { id: unknown }

  // Type check: id must be string or number (safely convertible to string)
  if (typeof id === 'string') {
    return id
  }
  if (typeof id === 'number') {
    return String(id)
  }

  throw new Error(
    `Row ID must be string or number, got ${typeof id}: ${JSON.stringify(id)}`
  )
}

/** Create row drag end payload matching backend API expectations */
const createRowDragPayload = (params: RowDragEndEvent): RowDragEndPayload => {
  const rowId = getRowId(params.node.data)
  const targetIndex = params.overNode?.rowIndex ?? 0

  return {
    id: rowId,
    position: targetIndex + 1 // Convert to 1-based for backend
  }
}

/** Send Phoenix event with target routing and error handling */
const sendPhoenixEvent = (
  hook: ViewHook,
  target: string | null | undefined,
  eventName: string,
  payload: unknown
): void => {
  try {
    if (target) {
      hook.pushEventTo(target, eventName, payload as object)
    } else {
      hook.pushEvent(eventName, payload)
    }
  } catch (error) {
    console.error(`Failed to send ${eventName} event:`, error)
  }
}

/** Reorder row in SSRM cache after drag */
const reorderRowInCache = (params: RowDragEndEvent): void => {
  const { api, node, overNode } = params

  if (!(overNode && node.data)) {
    return
  }

  const draggedRowData = node.data
  const targetIndex = overNode.rowIndex ?? 0

  // Remove the dragged row from its current position
  api.applyServerSideTransaction({ remove: [draggedRowData] })

  // Re-add the row at the target position
  api.applyServerSideTransaction({
    add: [draggedRowData],
    addIndex: targetIndex
  })
}

/** Create row drag end event handler for row reordering with Phoenix integration */
export const createRowDragEndHandler =
  (hook: ViewHook, target?: string | null, eventName?: string) =>
  (params: RowDragEndEvent): void => {
    try {
      // Validate required parameters
      if (!hasValidDragParams(params)) {
        console.warn(
          'Row drag end: missing required parameters (id and rowIndex)'
        )
        return
      }

      // Create and send payload
      const payload = createRowDragPayload(params)

      // Use provided event name or default to 'order'
      const event = eventName || 'order'
      sendPhoenixEvent(hook, target, event, payload)

      // For server-side model, manually reorder in cache for immediate visual feedback
      const modelType = params.api.getGridOption('rowModelType')
      if (modelType === 'serverSide') {
        reorderRowInCache(params)
      }

      // Clear drop indicator to prevent it from staying visible after drag ends
      params.api.setRowDropPositionIndicator({
        row: null,
        dropIndicatorPosition: null
      })
    } catch (error) {
      console.error('Row drag end handler failed:', error)
    }
  }
